import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import ImageSearch from '../../components/herramientas/ImageSearch';

type ExerciseType = 'word_selection' | 'gender_selection';

interface OptionDraft {
  text: string;
  isCorrect: boolean;
}

interface StimulusDraft {
  imagePath: string;
  correctAnswer: string;
  options: OptionDraft[];
  showSearch: boolean;
}

export default function HerramientasEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ExerciseType>('word_selection');
  const [stimuli, setStimuli] = useState<StimulusDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadExercise();
  }, [id]);

  const loadExercise = async () => {
    try {
      const res = await api.get(`/rehab/exercises/${id}`);
      if (!res.data.ok) throw new Error();
      const exercise = res.data.data;
      setTitle(exercise.title);
      setType(exercise.type);
      setStimuli(
        exercise.stimuli.map((s: any) => ({
          imagePath: s.image_path,
          correctAnswer: s.correct_answer,
          options: s.options.map((o: any) => ({ text: o.text, isCorrect: !!o.is_correct })),
          showSearch: false,
        }))
      );
    } catch {
      toast.error('Error al cargar el ejercicio');
      navigate('/herramientas');
    } finally {
      setLoading(false);
    }
  };

  const emptyStimulus = (): StimulusDraft => {
    if (type === 'gender_selection') {
      return {
        imagePath: '',
        correctAnswer: '',
        options: [{ text: 'EL', isCorrect: false }, { text: 'LA', isCorrect: false }],
        showSearch: false,
      };
    }
    return {
      imagePath: '',
      correctAnswer: '',
      options: Array(4).fill(null).map(() => ({ text: '', isCorrect: false })),
      showSearch: false,
    };
  };

  const removeStimulus = (index: number) => setStimuli(prev => prev.filter((_, i) => i !== index));

  const updateStimulus = (index: number, updates: Partial<StimulusDraft>) =>
    setStimuli(prev => prev.map((s, i) => i === index ? { ...s, ...updates } : s));

  const setCorrectOption = (stimIdx: number, optIdx: number) => {
    setStimuli(prev => prev.map((s, i) => {
      if (i !== stimIdx) return s;
      const options = s.options.map((o, j) => ({ ...o, isCorrect: j === optIdx }));
      return { ...s, options, correctAnswer: options[optIdx].text };
    }));
  };

  const updateOptionText = (stimIdx: number, optIdx: number, text: string) => {
    setStimuli(prev => prev.map((s, i) => {
      if (i !== stimIdx) return s;
      const options = s.options.map((o, j) => j === optIdx ? { ...o, text } : o);
      const correctAnswer = options.find(o => o.isCorrect)?.text ?? s.correctAnswer;
      return { ...s, options, correctAnswer };
    }));
  };

  const handleImageSelect = (stimIdx: number, url: string, label: string) => {
    setStimuli(prev => prev.map((s, i) => {
      if (i !== stimIdx) return s;
      let options = s.options;
      if (type === 'word_selection') {
        const firstEmpty = options.findIndex(o => !o.text);
        if (firstEmpty >= 0) options = options.map((o, j) => j === firstEmpty ? { ...o, text: label } : o);
      }
      const correctAnswer = options.find(o => o.isCorrect)?.text ?? s.correctAnswer;
      return { ...s, imagePath: url, options, correctAnswer, showSearch: false };
    }));
  };

  const validate = (): string | null => {
    if (!title.trim()) return 'El título es obligatorio';
    if (!stimuli.length) return 'Añade al menos un estímulo';
    for (let i = 0; i < stimuli.length; i++) {
      const s = stimuli[i];
      if (!s.imagePath) return `Estímulo ${i + 1}: falta la imagen`;
      if (!s.options.some(o => o.isCorrect)) return `Estímulo ${i + 1}: marca la respuesta correcta`;
      if (type === 'word_selection' && s.options.some(o => !o.text.trim()))
        return `Estímulo ${i + 1}: rellena todas las opciones`;
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) { toast.error(error); return; }

    setSaving(true);
    try {
      const payload = {
        title,
        stimuli: stimuli.map(s => ({
          imagePath: s.imagePath,
          correctAnswer: s.correctAnswer,
          options: s.options,
        })),
      };
      await api.put(`/rehab/exercises/${id}`, payload);
      toast.success('Ejercicio guardado');
      navigate('/herramientas');
    } catch {
      toast.error('Error al guardar el ejercicio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Link to="/herramientas" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Herramientas
          </Link>
          <h1 className="text-3xl font-display font-bold">Editar ejercicio</h1>
          <p className="text-white/60 text-sm mt-1">Tipo: {type === 'word_selection' ? 'Selección de palabra' : 'Género (EL / LA)'} (no editable)</p>
        </motion.div>

        <div className="space-y-6">
          {/* Título */}
          <div className="glass-container">
            <label className="block text-sm font-medium text-white/70 mb-2">Título del ejercicio</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-neureka-yellow"
            />
          </div>

          {/* Estímulos */}
          {stimuli.map((stim, stimIdx) => (
            <motion.div key={stimIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-container space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Estímulo {stimIdx + 1}</h3>
                {stimuli.length > 1 && (
                  <button type="button" onClick={() => removeStimulus(stimIdx)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  {stim.imagePath ? (
                    <img src={stim.imagePath} alt="estímulo" className="w-16 h-16 object-contain rounded-lg bg-white/10 p-1" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-white/30" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => updateStimulus(stimIdx, { showSearch: !stim.showSearch })}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    {stim.showSearch ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {stim.imagePath ? 'Cambiar imagen' : 'Buscar imagen'}
                  </button>
                </div>
                {stim.showSearch && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <ImageSearch onSelect={(url, label) => handleImageSelect(stimIdx, url, label)} />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {type === 'word_selection' ? 'Opciones (marca la correcta)' : 'Género correcto'}
                </label>
                <div className={`grid gap-2 ${type === 'gender_selection' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {stim.options.map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCorrectOption(stimIdx, optIdx)}
                        className={`w-5 h-5 rounded-full border-2 shrink-0 transition-colors ${
                          opt.isCorrect ? 'border-green-400 bg-green-400' : 'border-white/40 hover:border-white/60'
                        }`}
                      />
                      {type === 'word_selection' ? (
                        <input
                          type="text"
                          value={opt.text}
                          onChange={e => updateOptionText(stimIdx, optIdx, e.target.value)}
                          placeholder={`Opción ${optIdx + 1}`}
                          className="flex-1 px-2 py-1.5 bg-white/10 border border-white/20 rounded text-sm text-white placeholder-white/40 focus:outline-none focus:border-neureka-yellow"
                        />
                      ) : (
                        <span className="text-lg font-bold">{opt.text}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}

          <button
            type="button"
            onClick={() => setStimuli(prev => [...prev, emptyStimulus()])}
            className="w-full py-3 border-2 border-dashed border-white/20 hover:border-white/40 rounded-lg text-white/60 hover:text-white/80 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Añadir estímulo
          </button>

          <div className="flex gap-3">
            <Link to="/herramientas" className="flex-1 py-3 text-center border border-white/20 rounded-lg text-white/70 hover:text-white transition-colors">
              Cancelar
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 bg-neureka-yellow text-neureka-dark font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
